/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaModule, MtmaModuleSetting, MtmaSettingsModules } from 'src/app/model/mtma-module';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MtmaTask } from 'src/app/model/mtma-task';
import { selecMtmaSettingsModules, selectMtmaActiveTask, selectMtmaEditModule } from 'src/app/store/device/device.selector';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-mtma-manage-module-kb',
	templateUrl: './mtma-manage-module-kb.component.html',
	animations: [],
})

export class MtmaManageModuleKbComponent implements OnInit, OnDestroy {
	mtmaSettingsModules$: Observable<MtmaSettingsModules[] | undefined>;
	mtmaSettingsModulesSub: Subscription | undefined;
	mtmaActiveTask$: Observable < MtmaTask | undefined >;
	mtmaActiveTaskSub: Subscription | undefined;
	editModule$: Observable<boolean | undefined>;
	editModuleSub: Subscription | undefined;
	
	module: MtmaModule;

	moduleForm: FormGroup;

	isActiveTask = false;
	mtmacConfig: MtmaModuleSetting[];

	ref: DynamicDialogRef | undefined;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private formBuilder: FormBuilder,
		private translate: TranslateService,
		public config: DynamicDialogConfig,
		private dialogRef: DynamicDialogRef,
		public dialogService: DialogService,
	) { 
		if (this.config.data !== undefined && this.config.data.module !== undefined) {
			this.module = this.config.data.module;
		}
		this.initSelectors();
	}

	initSelectors(): void{
		this.mtmaSettingsModules$ = this.store.select(selecMtmaSettingsModules);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
		this.editModule$ = this.store.select(selectMtmaEditModule);
	}

	ngOnInit(): void {
		this.mtmaSettingsModulesSub = this.mtmaSettingsModules$.subscribe(list => {
			const module = list.filter(item => item.ModuleType === 4)[0];
			this.mtmacConfig = module.ListSettings;
		});
		const formGroup = {
			Name: [this.module.Name, [Validators.required, Validators.maxLength(48)]],
		};
		for (const setting of this.mtmacConfig) {
			if (setting.Type==='bits'){
				let listComp =[];
				let value = this.module.Config[setting.Name];
				for (const val of setting.Values.reverse()){
					if (value !== 0 && value/ (2 ** val.Value)>=1){
						listComp.push(val.Value);
						value = value - (2 ** val.Value);
					}
				}
				listComp =  listComp.reverse();
				formGroup[setting.Name] = [listComp];
			} else if (setting.Type === 'intset' || setting.Type === 'bool') {
				formGroup[setting.Name] = [setting.Values.find(set => set.Value === this.module.Config[setting.Name])];
			} else {
				formGroup[setting.Name] = [this.module.Config[setting.Name]];
			}
		}
		this.moduleForm = this.formBuilder.group(formGroup);
		this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
			if (mtmaActiveTask.Id!==''){
				this.isActiveTask = true;
			} else {
				this.isActiveTask = false;
			}
		});
		this.editModuleSub = this.editModule$.subscribe(editModule => {
			if (editModule){
				this.closeDialog();
			} 
		});

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.mtmaSettingsModulesSub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.editModuleSub?.unsubscribe();
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.moduleForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.moduleForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		this.module = {
			...this.module,
			Name: this.f.Name.value,
		};
		for (const setting of this.mtmacConfig) {
			if (setting.Type === 'intset' || setting.Type === 'bool'){
				this.module.Config[setting.Name]=this.moduleForm.controls[setting.Name].value.Value;
			} else {
				this.module.Config[setting.Name]=this.moduleForm.controls[setting.Name].value;
			}
		}
		this.store.dispatch(DeviceActions.editMtmaModule({ module: this.module }));
	}

	getTranslate(name: string, forceTraslate: boolean): string {
		if (name.includes('{') || forceTraslate){
			return this.translate.instant('MTMA_CONF_'+name);
		}else{
			return name;
		}
	}

	//-----SLIDER

	getValueFloat(value: number): number {
		return parseFloat(value.toFixed(1));
	}

	getValueString(value: number, scale: number): string {
		if (Math.floor(scale) === 1) {
			return value.toFixed(0);
		}
		return value.toFixed(1);
	}

	scaledValueFloat(value: number, scale: number): number {
		return this.getValueFloat(value * scale);
	}

	scaledValueString(value: number, scale: number): string {
		return this.getValueString(value * scale, scale).replace(".",",");
	}

	formattedSliderMin(setting: MtmaModuleSetting): string {
		return this.scaledValueString(setting.Min, 1);
	}

	formattedSliderMax(setting: MtmaModuleSetting): string {
		return this.scaledValueString(setting.Max, 1);
	}

	isSliderMin(setting: MtmaModuleSetting): boolean {
		return this.moduleForm.controls[setting.Name].value <= setting.Min;
	}

	isSliderMax(setting: MtmaModuleSetting): boolean {
		return this.moduleForm.controls[setting.Name].value >= setting.Max;
	}

	onChangeInputSlider(setting: MtmaModuleSetting){
		this.f[setting.Name].setValue(this.f[setting.Name].value);
	}

	onChangeSlider(setting: MtmaModuleSetting){
		this.f[setting.Name].setValue(this.f[setting.Name].value);
	}

	setSliderValue(setting: MtmaModuleSetting, value: number): boolean {
		let newValue = this.f[setting.Name].value + value * setting.StepSize;
		newValue = this.getValueFloat(newValue);
		if (newValue < setting.Min) {
			return false;
		}
		if (newValue > setting.Max) {
			return false;
		}
		// const oldValue = this.f[setting.Id].value;
		newValue = this.scaledValueFloat(newValue, 1);
		this.f[setting.Name].setValue(newValue);
		this.f[setting.Name].markAsDirty();
		return true;
	}
	//-----SLIDER

	isSettingsVisible(setting: MtmaModuleSetting): boolean {
		if (setting.Name === 'CodeLen' && this.f.UseMode.value.Value === 0){
			return false;
		}

		return true;
	}

	get f(): any {
		return this.moduleForm.controls;
	}

	closeDialog(): void {
		this.dialogRef.close();
	}
	
	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	col12Responsive() : string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth() : string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth() : string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth,'w-20rem');
	}

	dynamicInputTextDesktopTabletWidth() : string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-30rem', 'w-20rem');
	}

	justifyMessagesNewValidator() : string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaManageModuleKbComponent["f"], classType: string) : string {
		return StandardNewValidator.newShowErrors(type,classType,this.screenWidth);
	}

	dynamicAutocomplete() : string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}

	dynamicBorderLabelMobile(classType: string) : string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaManageModuleKbComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	justifyContentStyle() : string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}
}

